import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import BackButton from "../../components/common/BackButton";
import { getCurrencySymbol } from "../../helper/formatCurrency";
import { useRestaurantConfig } from "../../utils/restaurantConfig";
import api, { API_BASE } from "../../utils/api";

const emptyForm = { name: { en: "" }, price: "", isVisible: true, image: null, imageUrl: "" };

// name is a multi-language map ({ en: "Cheese", ar: "جبنة" }) coming back
// from the API, but may still be a plain string for anything not yet
// migrated - always fall back gracefully for display.
const displayName = (name) => {
  if (!name) return "";
  if (typeof name === "string") return name;
  return name.en || Object.values(name)[0] || "";
};

const AddOnsPage = () => {
  const { restaurantId, locale } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);

  const [addOns, setAddOns] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [imagePreview, setImagePreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadAddOns = async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const res = await api.get(`/addons?restaurantId=${restaurantId}`);
      setAddOns(res.data?.data || []);
    } catch (err) {
      console.error("Failed loading add-ons", err);
      toast.error("Failed to load add-ons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAddOns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  useEffect(() => {
    api
      .get("/languages/availableLanguages")
      .then((res) => setLanguages(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Failed loading languages", err));
  }, []);

  const openAddForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setImagePreview(null);
    setShowForm(true);
  };

  const openEditForm = (addOn) => {
    setEditingId(addOn._id);
    const name =
      typeof addOn.name === "string" ? { en: addOn.name } : { ...addOn.name };
    setForm({
      name,
      price: String(addOn.price),
      isVisible: addOn.isVisible,
      image: null,
      imageUrl: addOn.imageUrl || "",
    });
    setImagePreview(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setImagePreview(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((prev) => ({ ...prev, image: file }));
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const name = Object.fromEntries(
      Object.entries(form.name || {}).map(([lang, value]) => [lang, (value || "").trim()]),
    );
    const price = Number(form.price);

    if (!name.en) {
      toast.error("Name (English) is required");
      return;
    }
    if (Number.isNaN(price) || price < 0) {
      toast.error("Enter a valid price");
      return;
    }

    setSaving(true);
    try {
      const data = new FormData();
      data.append("name", JSON.stringify(name));
      data.append("price", price);
      data.append("isVisible", form.isVisible);
      if (form.image instanceof File) {
        data.append("image", form.image);
      }

      if (editingId) {
        await api.put(`/addons/${editingId}`, data);
        toast.success("Add-on updated");
      } else {
        data.append("restaurantId", restaurantId);
        await api.post(`/addons`, data);
        toast.success("Add-on created");
      }
      closeForm();
      loadAddOns();
    } catch (err) {
      console.error("Save add-on error:", err);
      toast.error(err.response?.data?.message || "Failed to save add-on");
    } finally {
      setSaving(false);
    }
  };

  const toggleVisible = async (addOn) => {
    try {
      await api.put(`/addons/${addOn._id}`, { isVisible: !addOn.isVisible });
      setAddOns((prev) =>
        prev.map((a) =>
          a._id === addOn._id ? { ...a, isVisible: !a.isVisible } : a,
        ),
      );
    } catch (err) {
      console.error("Toggle add-on error:", err);
      toast.error("Failed to update add-on");
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/addons/${deleteTarget._id}`);
      toast.success("Add-on deleted");
      setDeleteTarget(null);
      loadAddOns();
    } catch (err) {
      console.error("Delete add-on error:", err);
      toast.error(err.response?.data?.message || "Failed to delete add-on");
    }
  };

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <BackButton to="/dashboard" />
          <h1 className="text-lg font-bold text-gray-900">Manage Add-ons</h1>
        </div>
        <button
          onClick={openAddForm}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg shadow-sm transition-colors flex items-center gap-1.5"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Add-on
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center text-sm text-gray-400 py-10">Loading...</div>
        ) : addOns.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-10">
            No add-ons yet. Click "Add Add-on" to create one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700"></th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Name</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Price</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">Visible</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {addOns.map((addOn) => (
                <tr key={addOn._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    {addOn.imageUrl ? (
                      <img
                        src={`${API_BASE}${addOn.imageUrl}`}
                        alt={displayName(addOn.name)}
                        className="w-10 h-10 object-cover rounded-lg border border-gray-200"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gray-100" />
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-800">{displayName(addOn.name)}</td>
                  <td className="px-4 py-3 text-right text-gray-800">
                    {currencySymbol}
                    {Number(addOn.price).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => toggleVisible(addOn)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${addOn.isVisible ? "bg-green-500" : "bg-gray-300"
                        }`}
                      title={addOn.isVisible ? "Visible to customers" : "Hidden"}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${addOn.isVisible ? "translate-x-4.5" : "translate-x-1"
                          }`}
                        style={{ transform: addOn.isVisible ? "translateX(18px)" : "translateX(4px)" }}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEditForm(addOn)}
                        className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                        title="Edit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setDeleteTarget(addOn)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Form Dialog */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl w-[90%] max-w-[400px] mx-4">
            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">
                  {editingId ? "Edit Add-on" : "Add Add-on"}
                </h3>
              </div>
              <div className="px-6 py-4 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name (English)</label>
                  <input
                    type="text"
                    autoFocus
                    value={form.name.en || ""}
                    onChange={(e) =>
                      setForm({ ...form, name: { ...form.name, en: e.target.value } })
                    }
                    placeholder="e.g. Extra Cheese"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                {languages
                  .filter((lang) => lang.code !== "en")
                  .map((lang) => (
                    <div key={lang.code}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name ({lang.name})
                      </label>
                      <input
                        type="text"
                        value={form.name[lang.code] || ""}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            name: { ...form.name, [lang.code]: e.target.value },
                          })
                        }
                        dir={lang.direction || "ltr"}
                        placeholder={`Name in ${lang.name}`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  ))}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                  {(imagePreview || form.imageUrl) && (
                    <img
                      src={imagePreview || `${API_BASE}${form.imageUrl}`}
                      alt="Add-on preview"
                      className="w-16 h-16 object-cover rounded-lg mb-2 border border-gray-200"
                    />
                  )}
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/jpg, image/gif"
                    onChange={handleFileChange}
                    className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.isVisible}
                    onChange={(e) => setForm({ ...form, isVisible: e.target.checked })}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Visible to customers
                </label>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg shadow-md transition-colors"
                >
                  {editingId ? "Save Changes" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl w-[90%] max-w-[400px] mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Delete Add-on</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700">
                Delete "{displayName(deleteTarget.name)}"? It will be removed from any products
                that currently offer it.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium rounded-lg shadow-md transition-all"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddOnsPage;
